
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import Actions from '../../../../components/docs/Actions';
import Layout from '../../../../components/docs/Layout';
export const metadata = {
  feedback: true,
  toc: true,
  layout: 'guide',
  description: 'Learn how to support Auth for all users using our fast and secure flows, also known as Longtail Auth, Extended Auth, and Fallback Auth.',
  preloadImages: [{
    src: '/assets/img/docs/auth-overview-diagram.png',
    type: 'image/webp'
  }]
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const YouTubeEmbed = makeShortcode("YouTubeEmbed");
const Callout = makeShortcode("Callout");
const Image = makeShortcode("Image");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <Header title='Additional verification flows' subtitle='Support Auth for more US financial institutions and users' mdxType="Header" />
    <h4 {...{
      "id": "overview"
    }}>{`Overview`}</h4>
    <p>{`Instant Auth covers approximately 95% of users' eligible bank accounts.
For the remaining ~5%, or users who choose not to use instant credential verification, Plaid offers users the ability to
verify their accounts through an optimized set of database validation and micro-deposit flows.`}</p>
    <YouTubeEmbed videoId='FlZ5nzlIq74' alignment='full' title='Plaid Auth Tutorial' caption='Our video tutorial also provides a comprehensive guide to adding Auth using micro-deposits' mdxType="YouTubeEmbed" />
    <p>{`The following flows are enabled in your app by default once you have integrated Auth:`}</p>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/docs/auth/coverage/instant"
        }}>{`Instant Auth`}</a>{`: User enters their credentials and is authenticated immediately. This is the default flow. `}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/docs/auth/coverage/instant#instant-match"
        }}>{`Instant Match`}</a>{` (US only): User enters their credentials, account number, and routing number. Plaid matches user input against masked values and authenticates immediately. `}</li>
    </ul>
    <p>{`To increase conversion, you can enable the following additional Auth flows:`}</p>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/docs/auth/coverage/automated"
        }}>{`Automated Micro-deposits`}</a>{` (US only): User enters their credentials, account number, and routing number. Plaid makes a micro-deposit and automatically verifies the deposit in as little as one to two business days.`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/docs/auth/coverage/database-auth"
        }}>{`Database Auth`}</a>{` (US only): User enters account and routing numbers. Plaid attempts to match this data against known-good account and routing numbers recognized on the Plaid network. `}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/docs/auth/coverage/instant#instant-micro-deposits"
        }}>{`Instant Micro-deposits`}</a>{` (US only): User enters account and routing numbers. Plaid makes a RTP or FedNow micro-deposit and the user manually verifies the code in as little as 5 seconds.`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "/docs/auth/coverage/same-day"
        }}>{`Same Day Micro-deposits`}</a>{` (US only): User enters account and routing numbers. Plaid makes a Same Day ACH micro-deposit and the user manually verifies the code in as little as one business day.`}</li>
    </ul>
    <Callout mdxType="Callout">
      <p>{`To see each of these flows in action, check out the `}<a parentName="p" {...{
          "href": "https://plaid.coastdemo.com/share/67d0ce0df465686c02cc4fd2?zoom=100"
        }}>{`Auth workflows demo`}</a>{`.`}</p>
    </Callout>
    <h5 {...{
      "id": "auth-flow-method-comparison-chart"
    }}>{`Auth flow method comparison chart`}</h5>
    <Layout.FullWidth>
      <table>
        <thead parentName="table">
          <tr parentName="thead">
            <th parentName="tr" {...{
              "align": null
            }}>{`Method`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`Best for`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`Verification timeframe`}</th>
            <th parentName="tr" {...{
              "align": null
            }}>{`Requires additional integration work`}</th>
          </tr>
        </thead>
        <tbody parentName="table">
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><a parentName="td" {...{
                "href": "/docs/auth/coverage/instant"
              }}>{`Instant Auth`}</a></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`All risk profiles`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Instant`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`No (enabled by default)`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><a parentName="td" {...{
                "href": "/docs/auth/coverage/instant/#instant-match"
              }}>{`Instant Match`}</a></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`All risk profiles`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Instant`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`No (enabled by default)`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><a parentName="td" {...{
                "href": "/docs/auth/coverage/automated"
              }}>{`Automated Micro-deposits`}</a></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`All risk profiles`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`1-2 business days`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`No, but requires "pending verification" UX state in app`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><a parentName="td" {...{
                "href": "/docs/auth/coverage/instant#instant-micro-deposits"
              }}>{`Instant Micro-deposits`}</a></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Low and medium risk profiles`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Instant`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`No`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><a parentName="td" {...{
                "href": "/docs/auth/coverage/same-day"
              }}>{`Same Day Micro-deposits`}</a></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Low and medium risk profiles`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`1-2 business days, plus wait for user interaction`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Yes, and requires "pending verification" UX state in app`}</td>
          </tr>
          <tr parentName="tbody">
            <td parentName="tr" {...{
              "align": null
            }}><a parentName="td" {...{
                "href": "/docs/auth/coverage/database-auth"
              }}>{`Database Auth`}</a></td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Low and medium risk profiles`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`Instant`}</td>
            <td parentName="tr" {...{
              "align": null
            }}>{`No, unless used in advanced configuration mode`}</td>
          </tr>
        </tbody>
      </table>
    </Layout.FullWidth>
    <Callout warning mdxType="Callout">
      <p>{`Accounts verified via Database Auth, Instant Micro-deposits, or Same-Day Micro-deposits do not have active data connections with a financial institution. These accounts and their associated Items can only be used with Auth and Transfer, not with any other Plaid products (such as Balance or Transactions), with the partial exception of `}<a parentName="p" {...{
          "href": "/docs/identity/#using-identity-match-with-micro-deposit-or-database-items"
        }}>{`Identity Match`}</a>{` and `}<a parentName="p" {...{
          "href": "/docs/signal/signal-rules/#data-availability-limitations"
        }}>{`Signal`}</a>{`.`}</p>
      <p>{`Because of this limitation, accounts verified via these flows may be more vulnerable to fraud or return risk. Consider your level of risk exposure and current anti-fraud measures before enabling these methods. For more details, see `}<a parentName="p" {...{
          "href": "/docs/auth/coverage/same-day-link-best-practices"
        }}>{`Anti-fraud best practices`}</a>{`.`}</p>
    </Callout>
    <h4 {...{
      "id": "entry-points"
    }}>{`Entry points`}</h4>
    <p>{`To learn more about when Plaid presents Database Auth, Instant Micro-deposits, or Same-Day Micro-deposits as options, and to customize the Link user experience around these flows, see `}<a parentName="p" {...{
        "href": "/docs/auth/coverage/flow-options/"
      }}>{`Configuring entry points`}</a>{`.`}</p>
    <h4 {...{
      "id": "integration-instructions"
    }}>{`Integration instructions`}</h4>
    <p>{`Most additional Auth flows can be enabled in the `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/account-verification"
      }}>{`Account Verification Dashboard`}</a>{` with no additional integration work, as long as the following requirements are met:`}</p>
    <ul>
      <li parentName="ul">{`The `}<inlineCode parentName="li">{`country_codes`}</inlineCode>{` array in `}<inlineCode parentName="li">{`/link/token/create`}</inlineCode>{` is set to `}<inlineCode parentName="li">{`["US"]`}</inlineCode>{`. Additional Auth flows are not available if other countries are selected.`}</li>
      <li parentName="ul">{`The `}<inlineCode parentName="li">{`products`}</inlineCode>{` array does not contain any products other than `}<inlineCode parentName="li">{`auth`}</inlineCode>{` or `}<inlineCode parentName="li">{`transfer`}</inlineCode>{`. Any other products you want to use must be configured in the `}<inlineCode parentName="li">{`additional_consented_products`}</inlineCode>{`, `}<inlineCode parentName="li">{`required_if_supported_products`}</inlineCode>{`, or `}<inlineCode parentName="li">{`optional_products`}</inlineCode>{` arrays and will be used on a best-effort basis.`}</li>
    </ul>
    <p><a parentName="p" {...{
        "href": "/docs/auth/coverage/same-day"
      }}>{`Same-Day Micro-deposits`}</a>{` requires additional integration work, as you will need to present a Link UI where the end user can verify the micro-deposit description. For more details, see `}<a parentName="p" {...{
        "href": "/docs/auth/coverage/same-day"
      }}>{`Same-Day micro-deposits`}</a>{`.`}</p>
    <p><a parentName="p" {...{
        "href": "/docs/auth/coverage/database-auth"
      }}>{`Database Auth`}</a>{`, if used in the advanced mode where fallback rules are not configured in the Dashboard, requires additional integration work. For more details, see `}<a parentName="p" {...{
        "href": "/docs/auth/coverage/database-auth"
      }}>{`Database Auth`}</a>{`.`}</p>
    <Image src='/assets/img/docs/auth/av_dashboard_config.png' alt="Alt text: 'Account Verification Dashboard with settings for instant verification methods, additional methods toggle, and micro-deposit options.'" expandable='true' caption='Example of some configuration options available in the Account Verification Dashboard' mdxType="Image" />
    <h4 {...{
      "id": "institution-coverage"
    }}>{`Institution coverage`}</h4>
    <p>{`To see which Auth flows a given institution supports, you can call `}<inlineCode parentName="p">{`/institutions/get`}</inlineCode>{` with `}<a parentName="p" {...{
        "href": "/docs/api/institutions/#institutions-get-request-options-include-auth-metadata"
      }}><inlineCode parentName="a">{`options.include_auth_metadata`}</inlineCode></a>{` set to `}<inlineCode parentName="p">{`true`}</inlineCode>{`. The results will be returned in the `}<a parentName="p" {...{
        "href": "/docs/api/institutions/#institutions-get-response-institutions-auth-metadata-supported-methods"
      }}><inlineCode parentName="a">{`auth_metadata.supported_methods`}</inlineCode>{` object`}</a>{` in the response. Alternatively, you can see this information on the `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/activity/status"
      }}>{`Institution Status page`}</a>{` in the Plaid Dashboard. The Same Day Micro-deposits and Database Auth flows will not appear in these results, as they do not depend on institution capabilities and are available at all institutions. `}</p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "overview",
        level: 4,
        title: "Overview",
      },
{
        id: "entry-points",
        level: 4,
        title: "Entry points",
      },
{
        id: "integration-instructions",
        level: 4,
        title: "Integration instructions",
      },
{
        id: "institution-coverage",
        level: 4,
        title: "Institution coverage",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
